import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminCreateConsultantHourPackage,
  useAdminGetStripeProducts,
  getAdminGetConsultantHourPackagesQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { ConsultantHourPackageFields } from './ConsultantHourPackageFields';
import { formatMinorUnits } from '@/lib/format';
import {
  createConsultantHourPackageSchema,
  type CreateConsultantHourPackageForm,
} from '@/schemas/consultant-hour-package';

interface AddConsultantHourPackageDialogProps {
  productId: string;
  /** Stripe prices already offered by this product, excluded from the picker. */
  usedStripePriceIds: string[];
}

const defaultValues: CreateConsultantHourPackageForm = {
  stripePriceId: '',
  displayName: '',
  hours: 0,
  sortOrder: 0,
};

/**
 * Dialog for adding a consultant hour package to a product: pick a synced, active, one-time Stripe
 * price and set the hours credited per purchase.
 */
export function AddConsultantHourPackageDialog({
  productId,
  usedStripePriceIds,
}: AddConsultantHourPackageDialogProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { data: productsData } = useAdminGetStripeProducts(
    { includeInactive: false },
    { query: { enabled: isOpen } }
  );

  // Only active one-time prices can back a package; recurring prices are for subscriptions.
  const priceOptions = useMemo(
    () =>
      (productsData?.data?.products ?? []).flatMap((product) =>
        (product.prices ?? [])
          .filter((price) => price.active && price.isOneTime)
          .filter((price) => !usedStripePriceIds.includes(price.stripePriceId))
          .map((price) => ({
            stripePriceId: price.stripePriceId,
            label: `${product.name} — ${formatMinorUnits(price.unitAmount, price.currency)}`,
          }))
      ),
    [productsData, usedStripePriceIds]
  );

  const form = useForm<CreateConsultantHourPackageForm>({
    resolver: zodResolver(createConsultantHourPackageSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = form;

  const { mutate, isPending } = useAdminCreateConsultantHourPackage({
    mutation: {
      onSuccess: () => {
        toast.success('Consultant hour package created');
        setIsOpen(false);
        reset(defaultValues);
        queryClient.invalidateQueries({
          queryKey: getAdminGetConsultantHourPackagesQueryKey(productId),
        });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'CreateConsultantHourPackage', productId });
        toast.error('Failed to create package. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const onSubmit = useCallback(
    (data: CreateConsultantHourPackageForm) => {
      mutate({ productId, data });
    },
    [mutate, productId]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        reset(defaultValues);
      }
    },
    [reset]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Package
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Consultant Hour Package</DialogTitle>
          <DialogDescription>
            Offer a bundle of consultant hours for a one-time Stripe price. Create the price in
            Stripe and sync products first.
          </DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="packageStripePrice">Stripe Price</Label>
              {priceOptions.length === 0 ? (
                <div className="text-sm text-muted-foreground p-2 border rounded-md">
                  No unused one-time prices found. Create a one-time price in Stripe, then sync
                  products.
                </div>
              ) : (
                <Select
                  value={watch('stripePriceId')}
                  onValueChange={(value) =>
                    setValue('stripePriceId', value, { shouldValidate: true })
                  }
                >
                  <SelectTrigger id="packageStripePrice">
                    <SelectValue placeholder="Select a one-time price" />
                  </SelectTrigger>
                  <SelectContent>
                    {priceOptions.map((option) => (
                      <SelectItem key={option.stripePriceId} value={option.stripePriceId}>
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          <span className="text-xs text-muted-foreground font-mono">
                            {option.stripePriceId}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {errors.stripePriceId && (
                <p className="text-sm text-destructive">{errors.stripePriceId.message}</p>
              )}
            </div>
            <ConsultantHourPackageFields />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Creating...' : 'Create Package'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
